<template>
    <div
    ref="mobileFeatureContentContainer"
    class="features-base-content-wrapper"
    :class="[{
        'mobile': getMobileAppState,
        'non-authenticated-feature': nonAuthenticatedFeature && !getMobileAppState,
        'third-level-menu': thirdLevelMenu
    }, applyFeedbackOperationStatusClass]"
    :style="{'padding-bottom': getKeyboardHeight && getDeviceInUse !== 'ios' ? getKeyboardHeight : '' }"
    @scroll="[mobileValueScrolled = $event.target.scrollTop, emit('mobileScrollValueUpdated', $event.target.scrollTop)]">
        <!-- Feature Header -->
        <navigation-header-base
        :nonAuthenticatedFeature="nonAuthenticatedFeature"
        :hideClose="hideClose"
        :centerLogo="centerLogo"
        :fullWhiteBox="fullWhiteBox"
        :valueScrolled="getMobileAppState ? mobileValueScrolled : valueScrolled"
        :customBreadcrumbs="customBreadcrumbs"
        :customHeaderTabs="customHeaderTabs"
        :hideTabs="hideTabs"
        :hideBreadcrumbsButShowOnCurtain="!!applyFeedbackOperationStatusClass"
        :hideHeaderButtons="hideHeaderButtons"
        :showStepper="showStepper"
        :showSearchSlot="showSearchSlot"
        :showTopRightAreaSlot="showTopRightAreaSlot"
        :customMobileBackIcon="operationsWrapper ? 'close' : customMobileBackIcon"
        @backOrCloseClicked="emit('backOrCloseClicked')"
        @headerHasTabs="headerHasTabs = $event"
        @customTabClicked="emit('customTabClicked', $event)"
        @parentBreadcrumbClicked="emit('parentBreadcrumbClicked')">
            <!-- Mobile top right button slot -->
            <template v-if="getMobileAppState && showTopRightButtonMobileSlot" #top-right-button-mobile-slot><slot name="top-right-button-mobile-slot"/></template>
            <!-- Slot for elements position on top area of header in web, and on top of content in mobile -->
            <template v-if="showTopRightAreaSlot" #accounts-card-slot-area><slot name="accounts-card-slot-area"/></template>
            <!-- Slot for search -->
            <template v-if="showSearchSlot" #top-search-area-slot><slot name="top-search-area-slot"/></template>
            <!-- Slot for stepper -->
            <template v-if="showStepper" #top-stepper-area-slot><slot name="top-stepper-area-slot"/></template>
            <!-- Slot For Tabs Extra info on slot -->
            <template v-if="showTabsExtraSlot" #tabs-slot-extra-content><slot name="tabs-slot-extra-content"/></template>
        </navigation-header-base>
        <!-- Feature Content -->
        <div
        ref="webFeatureContentContainer"
        class="feature-content-container"
        :class="[{
            'one-column-wrapper': oneColumnWrapper && !showLoader,
            'operations-wrapper': operationsWrapper && !showLoader,
            'white-background': fullWhiteBox,
            'show-filters-and-empty-state': showFiltersAndEmptyState,
            'mobile': getMobileAppState,
            'remove-border-radios': headerHasTabs || (showTopRightAreaSlot && getMobileAppState) || (showSearchSlot && (getMobileAppState || nonAuthenticatedFeature)),
            'no-padding-top': showSearchSlot,
            'no-after-bottom': getMobileAppState,
            'showing-footer': showingFooter,
        }, applyFeedbackOperationStatusClass]"
        :style="{'background-image': customBackgroundImage}"
        @scroll="[valueScrolled = $event.target.scrollTop, emit('scrollValueUpdated', $event.target.scrollTop)]">
            <slot></slot>
            <!-- Loader Generic -->
            <loaders-generic v-if="(showLoader || showOverlayLoader) && !applyErrorState" :isOverlay="showOverlayLoader"/>
            <!-- Empty-State-->
            <slot name="empty-state-slot"></slot>
            <!-- Error State -->
            <empty-state-error v-if="applyErrorState" :message="errorStateMessage"/>
        </div>
        <!-- Slot for Floating Button -->
        <slot name="floating-button-area-slot"></slot>
        <!-- Slot for Footer -->
        <slot name="footer-area-slot"></slot>
    </div>
    <!-- Token Modal -->
    <modal-centered-token
    v-if="getShowTokenModalDisplayState && !menuBase"
    :titleAppend="tokenTitleAppend"
    :titleFull="tokenTitleFull"
    :params="tokenParams"
    :modalCustomUseParams="tokenCustomUseParams"
    @hideMe="hideTokenChallengeModal()"
    @challengeCancelHandler="emit('challengeCancelHandler')"
    @challengeSuccessHandler="emit('challengeSuccessHandler', $event)"
    @challengeErrorHandler="emit('challengeErrorHandler', $event)"/>
</template>

<script setup>
import NavigationHeaderBase from '@/components/generic/navigation/header/Base'
import LoadersGeneric from '@/components/generic/loaders/Generic'
import EmptyStateError from '@/components/generic/emptyStates/Error'
import ModalCenteredToken from '@/components/generic/modals/centered/Token'
/* Vue */
import { ref, computed, useSlots, watch } from 'vue'
import { scrollTo } from '@/assets/scripts/scrollAnimation'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'

/* Emmitters */
const emit = defineEmits(['mobileScrollValueUpdated', 'scrollValueUpdated', 'backOrCloseClicked', 'floatingButtonClicked',
                        'customTabClicked', 'challengeSuccessHandler', 'challengeErrorHandler', 'challengeCancelHandler', 'clearScrollToValue', 'parentBreadcrumbClicked'])

/* Props
@oneColumnWrapper {Boolean} prop to apply background color to match content of one-column-wrapper features in mobile
@operationsWrapper {Boolean} prop to apply background color to match content of operations-wrapper features in mobile
@thirdLevelMenu {Boolean}
@nonAuthenticatedFeature {Boolean} prop to apply specific styles for this type of features
@hideClose {Boolean} prop to prevent close button from being shown in nonAuthenticatedFeatures or mobile
@centerLogo {Boolean} prop to center on nonAuthenticatedFeatures our logo on center
@fullWhiteBox {Boolean} prop to apply to content var(--secondary-light-4) as background color
@customBackgroundImage {string}
@showFiltersAndEmptyState {Boolean} - prop to appply correct sytles
@customBreadcrumbs {Object} - if our breadcrumbs are not direct from menu construction based on route, use this object
@scrollValue {Number} - new scrollValue position
@customHeaderTabs {Array} - array with custom tabs
@hideHeaderButtons {Boolean}
@hideTabs {Boolean} - prop to force tabs to not show, even if route has menu ones
@hideAfterBottom {Boolean} - prop to hide after space on bottom
@applyFeedbackOperationStatusClass {String} - prop with feedback operation status class to apply on header instead of app default gradient
@showStepper {Boolean} - prop to showStepper on curtain on mobile header
@showingFooter {Boolean}
@showLoader {Boolean} - prop to show loader
@showTopRightAreaSlot {Boolean}
@showSearchSlot {Boolean}
@customMobileBackIcon {String} - name of custom icon for close mobile button
@showOverlayLoader {Boolean} - prop to apply overlay loader
@applyErrorState {Boolean} - prop to apply Error State
@errorStateMessage {String} - message to display on errorState
@tokenTitleAppend {String} - string to appentd at token modal title
@tokenTitleFull {String} - string to use as token modal title
@tokenParams {Object} - some times token submit will have additional params to send with token, when so, send it in this prop
@tokenCustomUseParams {Object} - when we want to have this modal but with apis diferent than the generic ones we send this object with:
    - messageToDisplay {String}
    - sendTokenAction {string} - 'moduleName/actionName'
    - resendTokenAction {string} - 'moduleName/actionName'
    - payload {Object} info that we want to send along with the response
    - resendPayload {Object} with info needed to resent token
    - forceResendChannelString {String} - ['email', 'sms'] - prop to ignore session token channel and use custom one
*/
const props = defineProps({
    oneColumnWrapper: Boolean,
    operationsWrapper: Boolean,
    nonAuthenticatedFeature: Boolean,
    thirdLevelMenu: Boolean,
    hideClose: Boolean,
    centerLogo: Boolean,
    fullWhiteBox: Boolean,
    customBackgroundImage: String,
    showFiltersAndEmptyState: Boolean,
    customBreadcrumbs: Object,
    scrollValue: Number,
    customHeaderTabs: Array,
    hideHeaderButtons: Boolean,
    hideTabs: Boolean,
    hideAfterBottom: Boolean,
    applyFeedbackOperationStatusClass: String,
    showStepper: Boolean,
    showingFooter: Boolean,
    showSearchSlot: Boolean,
    showTopRightAreaSlot: Boolean,
    customMobileBackIcon: String,
    /* Loader Props */
    showLoader: Boolean,
    showOverlayLoader: Boolean,
    /* ErrorState Props */
    applyErrorState: Boolean,
    errorStateMessage: String,
    /* Token related Props */
    tokenTitleAppend: String,
    tokenTitleFull: String,
    tokenParams: Object,
    tokenCustomUseParams: Object
})

console.log(props.customBackgroundImage)

/* mapGetters */
const { hideTokenChallengeModal } = mapActions()
const { getMobileAppState, getDeviceInUse, getShowTokenModalDisplayState, getKeyboardHeight, getPreventMobileFeatureBackState } = mapGetters()

const valueScrolled = ref(0)
const mobileFeatureContentContainer = ref('')
const webFeatureContentContainer = ref('')
const mobileValueScrolled = ref(0)
const headerHasTabs = ref(false)

/*
* For overlay header on scroll,
* we need to make sure that our slots are not being populated, and if not, remove them with a v-if
*/
const showTopRightButtonMobileSlot = computed(() => {
    return useSlots()['top-right-button-mobile-slot']
})

const showTabsExtraSlot = computed(() => {
    return useSlots()['tabs-slot-extra-content']
})

watch(() => props.scrollValue, (newVal, oldVal) => {
    if (newVal || newVal === 0) {
        if (getMobileAppState.value) scrollTo(mobileFeatureContentContainer.value, props.scrollValue, null, 500, 'vertical')
        else scrollTo(webFeatureContentContainer.value, props.scrollValue, null, 500, 'vertical')
        emit('clearScrollToValue')
    }
})

const onBackKeyDown = () => {
    if (getPreventMobileFeatureBackState.value) return
    emit('backOrCloseClicked')
}

document.addEventListener('backbutton', onBackKeyDown, false)
</script>

<style lang="scss" scoped>
.features-base-content-wrapper {
    width: 100%;
    height: 100%;

    display: flex;
    flex-direction: column;
    overflow: hidden;

    .feature-header-placeholder {
        width: 100%;
        height: 104px;
        padding-left: 24px;

        flex-shrink: 0;

        line-height: 104px;
        background-color: var(--secondary-light-3);
    }

    .feature-content-container {
        width: 100%;

        background-color: var(--secondary-light-3);
        border-radius: 16px 16px 0 0;

        &.white-background {
            background-color: var(--secondary-light-4);

            &.remove-border-radios {
                :deep(.one-column-wrapper) {
                    padding-top: 0;
                }
            }
        }

        /* Some browsers ignore padding-bottom when no elements are render after it, we use an after to force this space */
        &:after {
            content: '_';
            width: 100%;
            height: 24px;

            visibility: hidden;
            display: inline-block;
        }

        &:not(.mobile) {
            height: 100%;
            padding: 24px 16px 0 24px;

            display: flex;
            flex-direction: column;

            overflow: auto;
            @include generic-scrollbar(transparent, var(--primary));
            scrollbar-gutter: stable; /* NEEDS IOS TESTING */

            &.no-padding-top {
                padding-top: 0;
            }

            &.showing-footer {
                @include generic-scrollbar(transparent, var(--primary), false, 90px);
                padding-bottom: 90px;
            }
        }

        :deep(.spinner-wrapper) {
            width: 100%;
            height: calc(100% - 72px);

            justify-content: center;
            display: flex;
            align-items: center;
        }

        :deep(.error-state-wrapper) {
            width: 100%;
            height: calc(100% - 24px);
        }

        :deep(.empty-state-wrapper) {
            @include commonEnteringAnimation;
            width: 100%;
            height: 100%;
        }

        &.show-filters-and-empty-state :deep(.empty-state-wrapper) {
            border-top-left-radius: 0;
            border-top-right-radius: 0;
        }
    }

    &.mobile, &.non-authenticated-feature {
        &:not(.non-authenticated-feature) {
            overflow: auto;
            overflow-x: hidden;

            .feature-content-container {
                &.one-column-wrapper {
                    background-image: linear-gradient(var(--secondary-light-3) 98px, var(--secondary-light-4) 5%);
                }

                &.operations-wrapper {
                    background-image: linear-gradient(var(--secondary-light-3) 5%, var(--secondary-light-4) 5%);
                }
            }

            &.third-level-menu {
                .feature-content-container {
                    &.one-column-wrapper {
                        background-image: unset;
                    }
                }
            }
        }

        &:before {
            content: '';
            width: 100%;
            height: 50%;
            position: absolute;

            background-image: var(--gradient-horizontal);
        }

        &.info, &.success {
            &:before {
                background-color: var(--positive);
                background-image: unset;
            }
        }

        &.error {
            &:before {
                background-color: var(--negative);
                background-image: unset;
            }
        }

        &.warning {
            &:before {
                background-color: var(--warning);
                background-image: unset;
            }
        }

        .feature-content-container {
            width: 100%;
            height: auto;
            padding-bottom: constant(safe-area-inset-bottom); /* iOS 11.0 */
            padding-bottom: env(safe-area-inset-bottom); /* iOS 11.2 */

            flex: 1;
            display: flex;
            flex-direction: column;

            z-index: 1;

            &.remove-border-radios {
                border-radius: 0;
            }

            &.no-after-bottom {
                /* Some browsers ignore padding-bottom when no elements are render after it, we use an after to force this space */
                &:after {
                    display: none;
                }
            }

            &.info, &.success {
                background-image: linear-gradient(var(--positive) 40%, var(--secondary-light-4) 10%);
            }

            &.error {
                background-image: linear-gradient(var(--negative) 40%, var(--secondary-light-4) 10%);
            }

            &.warning {
                background-image: linear-gradient(var(--warning) 40%, var(--secondary-light-4) 10%);
            }

            &.operations-wrapper {
                padding-top: 0;
                padding-right: 0;
                padding-left: 0;

                background-color: var(--secondary-light-4);
            }

            .accounts-card-slot-area {
                width: 100%;
                margin: 16px 0;
                padding: 0 16px;

                flex-shrink: 0;
            }
        }
    }

    &.mobile {
        &.one-column-wrapper {
            background-image: linear-gradient(var(--secondary-light-3) 5%, var(--secondary-light-4) 5%);
        }

        .feature-content-container {
            &.one-column-wrapper {
                &.showing-footer {
                    padding-bottom: 90px;
                }
            }
        }
    }
}
</style>
